import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import type {UserMessageProps} from './user-message.types';
import styles from './user-message.module.scss';
import MarkdownRenderer from '@Components/ai-chat-window/components/markdown-renderer/markdown-renderer';
import type {ChatMessagePart} from '@Components/ai-chat-window/ai-chat-window.types';
import {ChatMessagePartType, TextPartPurpose} from '@Components/ai-chat-window/ai-chat-window.types';
import {UserAttachment} from './components/user-attachment';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {EditMessageBox} from '@Components/ai-chat-window/components/edit-message-box/edit-message-box';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {getAiChatPromptBoxId} from '@Libraries/ai-chat.library';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {isImagePart} from '@Components/ai-chat-window/components/user-message-row/components/user-attachment/user-attachment.types';
import {BrandKitPreference} from './components/brand-kit-preference/brand-kit-preference';
import {RequestPreferenceCard} from './components/request-preference-card/request-preference-card';
import {getSeparatedStringFromCamelCase} from '@Utils/string.util';

function getMessageText(parts: ChatMessagePart[]): string {
  const textParts = parts.filter((p) => p.type === ChatMessagePartType.TEXT);
  return textParts.map((p) => p.text ?? '').join('\n');
}

function preserveLineBreaksForMarkdown(text: string): string {
  return text.replace(/\n/g, '  \n');
}

function UserMessageRowInner({messageKey, ...props}: UserMessageProps): ReactElement {
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);
  const [editValue, setEditValue] = useState('');
  const {state, actions} = useAiChat();
  const editingId = props.message.messageId ?? messageKey;
  const isEditing = props.chatId ? state.editingUserMessageIdMap[props.chatId] === editingId : false;
  const {actions: promptBoxActions} = useAiPromptBox(getAiChatPromptBoxId(props.chatId));
  const parts = props.message.parts ?? [];
  const attachmentParts = parts.filter(isImagePart);
  const textParts = parts.filter((part) => part.type === ChatMessagePartType.TEXT);

  useEffect(() => {
    if (isEditing) {
      setEditValue(getMessageText(props.message.parts ?? []));
    }
  }, [isEditing, props.message.parts]);

  const handleAttachmentImageClick = (uniqueKey: string): void => {
    props.onImageClick(props.message.messageId, uniqueKey);
  };

  const getAttachmentParts = (attachmentParts: ChatMessagePart[]): ReactElement => {
    return (
      <>
        {attachmentParts.map((part, index) => (
          <UserAttachment
            key={`user-part-${index}`}
            part={part}
            partKey={`user-part-${index}`}
            onImageClick={handleAttachmentImageClick}
            onReattach={props.onReattach}
            isMobile={props.isMobile}
          />
        ))}
      </>
    );
  };

  const getAttachments = (attachmentParts: ChatMessagePart[]): ReactElement => {
    return <div className={`flex-justify-end spacing-m-b-1 flexbox flex-align-items-center gap-2 ${styles.attachments}`}>{getAttachmentParts(attachmentParts)}</div>;
  };

  const handleCopyMessage = async (): Promise<void> => {
    const messageText = getMessageText(props.message.parts ?? []);
    const copyTooltipDurationMs = 3000;

    try {
      await navigator.clipboard.writeText(messageText);
      setShowCopiedTooltip(true);
      setTimeout(() => {
        setShowCopiedTooltip(false);
      }, copyTooltipDurationMs);
    } catch (error) {
      console.error('Failed to copy message to clipboard', error);
    }
  };

  const getEditButton = (): ReactElement | null => {
    if (props.message.deletedAt) {
      return null;
    }

    return (
      <OverlayTrigger
        overlay={
          <Tooltip className="spacing-m-b-2 _rounded-arrow">
            {props.isMessageStreaming ? window.i18next.t('pmwjs_cant_edit_while_streaming') : window.i18next.t('pmwjs_edit')}
          </Tooltip>
        }
        trigger={['hover']}
        placement="top"
        key={`tooltip-edit-message`}
      >
        <span>
          <Icon
            type={IconType.GHOST}
            shape={IconShape.CIRCLE}
            icon="icon-pencil"
            size={IconSize.SIZE_ICON_16}
            isDisabled={props.isMessageStreaming}
            onClick={(): void => {
              if (!props.isMessageStreaming) {
                promptBoxActions.update({promptBoxState: PromptBoxState.IDLE});
                actions.setEditingUserMessageId(editingId, props.chatId);
              }
            }}
          />
        </span>
      </OverlayTrigger>
    );
  };

  const getHandleCopyButton = (): ReactElement => {
    return (
      <OverlayTrigger
        overlay={<Tooltip className="spacing-m-b-2 _rounded-arrow">{showCopiedTooltip ? window.i18next.t('pmwjs_copied') : window.i18next.t('pmwjs_copy')}</Tooltip>}
        trigger={['hover']}
        placement="top"
        key={`tooltip-copy-message`}
      >
        <Icon
          type={IconType.GHOST}
          shape={IconShape.CIRCLE}
          icon="icon-copy"
          size={IconSize.SIZE_ICON_16}
          onClick={(): void => {
            void handleCopyMessage();
          }}
        />
      </OverlayTrigger>
    );
  };

  const getActionIcons = (): ReactElement | null => {
    return (
      <div
        className={`flex-justify-end flex-h-row flex-align-items-center ${!props.isMobile ? 'gap-1' : 'gap-2'} ${styles.actionIconsContainer} ${!props.isMobile ? styles.actionIconsDesktop : ''}`}
      >
        {getEditButton()}
        {getHandleCopyButton()}
      </div>
    );
  };

  const getEditMessageBox = (): ReactElement => {
    return (
      <div className={`flex-v-row ${styles.userMessage} ${styles.editMode} ${props.isMobile ? styles.userMessageSmall : ''}`}>
        {attachmentParts.length > 0 ? getAttachments(attachmentParts) : null}
        <EditMessageBox
          value={editValue}
          onChange={setEditValue}
          onCancel={(): void => {
            actions.setEditingUserMessageId(null, props.chatId);
          }}
          onSend={(): void => {
            props.onEditSend?.(props.message.messageId ?? messageKey, editValue);
          }}
          isLoading={props.editSendInProgress}
          promptBoxId={getAiChatPromptBoxId(props.chatId)}
        />
      </div>
    );
  };

  const getSuperUserPreferences = (): ReactElement | null => {
    const requestPreferences = props.message.requestPreferences;

    if (!state.isSuperUser || state.hideGenerationContext || !requestPreferences) {
      return null;
    }

    const {brandKitId, ...otherPreferences} = requestPreferences;
    const entries = Object.entries(otherPreferences).filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].length > 0);

    if (entries.length === 0 && !brandKitId) {
      return null;
    }

    const getBrandKitPreference = (): ReactElement | null => {
      if (!brandKitId) {
        return null;
      }
      return <BrandKitPreference brandKitId={brandKitId} />;
    };

    return (
      <div className={styles.superUserPreferences}>
        {entries.map(([preferenceKey, value]) => (
          <RequestPreferenceCard key={preferenceKey} label={getSeparatedStringFromCamelCase(preferenceKey)} value={value} />
        ))}
        {getBrandKitPreference()}
      </div>
    );
  };

  const getMessageContent = (): ReactElement => {
    return (
      <div className={`flex-v-row ${styles.userMessage} ${props.isMobile ? styles.userMessageSmall : ''} ${!props.isMobile ? styles.userMessageDesktop : ''}`}>
        {attachmentParts.length > 0 ? getAttachments(attachmentParts) : null}
        {textParts.map((part, index) => (
          <div
            key={`user-text-part-${index}`}
            className={`${props.bubbleClassNames} ${styles.userText} ${part.purpose === TextPartPurpose.CHANGE_STYLE ? styles.changeStyleText : ''}`}
          >
            <MarkdownRenderer content={preserveLineBreaksForMarkdown(part.text ?? '')} />
          </div>
        ))}
        {getSuperUserPreferences()}
        {getActionIcons()}
      </div>
    );
  };

  const renderContent = (): ReactElement => {
    if (isEditing) {
      return getEditMessageBox();
    } else {
      return getMessageContent();
    }
  };

  return renderContent();
}

export const UserMessageRow = React.memo(UserMessageRowInner);
